import React, { ReactNode, useCallback, useEffect, useRef, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';

import {
  Container,
  ContainerNav,
  RootModal,
  ContainerModal,
  ModalHeader,
  ModalContent,
} from './styles';
import BaseMenu from '../BaseMenu';
import NavBar from '../NavBar';
import Search from '../Search';
import Menu from '../Menu';
import { StoreState } from '../../store/createStore';
import {
  findStoreAll,
  openShowModal,
  setMessageToastClear,
  setMultistoreSelected,
  viewModal,
} from '../../store/modules/parameter/actions';
import { useToast } from '../../hooks/toast';
import { clearMessageCustomerCard, clearError } from '../../store/modules/checkout/actions';
import { clearMessage } from '../../store/modules/auth/actions';
import { IoMdClose } from 'react-icons/io';
import { useTranslation } from '../../hooks/useTranslation';
import { config } from '../../config';
import MultiStore from '../../components/Multistore';
import { Store } from '../../store/modules/parameter/types';

interface MasterProps {
  children: ReactNode;
}

interface ResizeObserverInstance {
  observe(target: Element): void;
  unobserve(target: Element): void;
  disconnect(): void;
}

declare global {
  interface Window {
    ResizeObserver?: {
      new (callback: (entries: Array<{ target: Element; contentRect: DOMRectReadOnly }>, observer: ResizeObserverInstance) => void): ResizeObserverInstance;
    };
  }
}

const Master: React.FC<MasterProps> = ({ children }: MasterProps) => {
  const dispatch = useDispatch();
  const { onTranslation: t } = useTranslation();
  const { 
    openModal, 
    viewModal: ModalBody, 
    viewModalProps, 
    messageToast,
    messageTranslationId,
    typeToast,
    multistoreSelected,
    stores
  } = useSelector(
    (state: StoreState) => state.parameter
  );
  const { messageToast: messageToastHook, onClearMessage } = useToast();
  const location = useLocation();
  const containerNavRef = useRef<HTMLDivElement>(null);
  const resizeObserverRef = useRef<ResizeObserverInstance | null>(null);
  const [navHeight, setNavHeight] = useState(0);
  const isHome = location.pathname === '/';  

  const handleModalShow = useCallback(() => {    
    dispatch(openShowModal(false));
  }, [dispatch]);

  const onCloseMessageToast = useCallback(async () => {
    if (messageToastHook) {
      onClearMessage();
    }
    dispatch(clearMessageCustomerCard());
    dispatch(setMessageToastClear());
    dispatch(clearError());
    dispatch(clearMessage());
  }, [dispatch, onClearMessage, messageToastHook]);

  useEffect(() => {
    if (
      (messageToast && messageToast?.trim() !== '' && typeToast) 
      || (messageToastHook && messageToastHook.description && messageToastHook.description.trim() !== '')
      || (messageTranslationId && messageTranslationId?.trim() !== '' && typeToast) 
      ) {
      const getMessage = () => {
        if (messageToastHook.type && messageToastHook.description) {
          return {
            typeToastSelected: messageToastHook.type,
            messageToastSelected: messageToastHook.description,
          }
        }
        return {
          typeToastSelected: typeToast,
          messageToastSelected: messageTranslationId && messageTranslationId.length > 1 ?
            t(messageTranslationId) : messageToast,
        }
      }
      const { 
        messageToastSelected,
        typeToastSelected,
      } = getMessage();
      
      if (typeToastSelected === 'success') {
        toast.success(messageToastSelected, {
          position: 'top-right',
          autoClose: 3000,
          onClose: () => onCloseMessageToast(),
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        });

      }
      else if (typeToastSelected === 'error') {
        toast.error(messageToastSelected, {
          position: 'top-right',
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          onClose: () => onCloseMessageToast(),
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        });
      }
      else if (typeToastSelected === 'warning') {
        toast.warning(messageToastSelected, {
          position: 'top-right',
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          onClose: () => onCloseMessageToast(),
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        });
      }
    }
  }, [messageToast, typeToast, onCloseMessageToast, messageToastHook, messageTranslationId, t]);

    useEffect(() => {
       if (config.isAllowedMultistore && !multistoreSelected.store_id) {
        dispatch(viewModal(MultiStore, { width: '50%', height: '100%', buttonClose: true }, true));
       }
    }, [multistoreSelected.store_id, dispatch]);

    useEffect(() => {
      dispatch(findStoreAll());
    }, [dispatch]);

   useEffect(() => {
    if (stores && stores.length > 0) {
      if (config.isAllowedMultistore) {
        const storeUpdated = stores.find(store => store.store_id === multistoreSelected.store_id);
        if (storeUpdated?.store_id) {
          dispatch(setMultistoreSelected(storeUpdated));
        } else {
          dispatch(setMultistoreSelected({} as Store));
        }
      } else {
        dispatch(setMultistoreSelected(stores[0]));
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [stores]);

  useEffect(() => {
    if (!isHome) {
      setNavHeight(0);
      return;
    }

    const containerNavElement = containerNavRef.current;

    const updateNavHeight = () => {
      if (containerNavRef.current) {
        setNavHeight(containerNavRef.current.offsetHeight);
      }
    };

    requestAnimationFrame(() => {
      updateNavHeight();
    });

    const setupResizeObserver = () => {
      if (containerNavRef.current && typeof window.ResizeObserver !== 'undefined') {
        if (resizeObserverRef.current) {
          resizeObserverRef.current.disconnect();
        }
        
        resizeObserverRef.current = new window.ResizeObserver(() => {
          updateNavHeight();
        }) as ResizeObserverInstance;
        resizeObserverRef.current.observe(containerNavRef.current);
      }
    };
    
    requestAnimationFrame(() => {
      setupResizeObserver();
    });

    window.addEventListener('resize', updateNavHeight);

    return () => {
      window.removeEventListener('resize', updateNavHeight);
      if (resizeObserverRef.current) {
        if (containerNavElement) {
          resizeObserverRef.current.unobserve(containerNavElement);
        }
        resizeObserverRef.current.disconnect();
        resizeObserverRef.current = null;
      }
    };
  }, [isHome]);

  return (
    <Container navHeight={isHome ? navHeight : 0}>
      {openModal && (
        <RootModal>
          <ContainerModal onClick={() => {
            if (!viewModalProps || !viewModalProps.buttonClose) {
              handleModalShow();
            }
          }}>
            <ModalHeader />
          </ContainerModal>
          <ModalContent parameter={viewModalProps}>
            {viewModalProps?.buttonClose ? (<></>)
              : (<button type="button" id="close-modal" onClick={handleModalShow}><IoMdClose size={32} color="#ccc" /></button>)}
            {ModalBody && <ModalBody />}
          </ModalContent>
        </RootModal>
      )}

      <ContainerNav ref={containerNavRef} isFixed={isHome}>
        <BaseMenu />
        <NavBar />
        <Search />
        <Menu />
      </ContainerNav>
      {children}
      {/* <ContainerFooter /> */}
    </Container>
  );
};

export default Master;
